import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/authApi';
import { BOT_URL } from '../config/telegram';

const POLL_MS = 3000;

/**
 * Linking the signed-in account to Telegram: a one-time code, the deep link
 * that carries it (t.me/bot?start=CODE: tapping "Iniciar" sends the code, and
 * the bot links once the person taps "Conectar" under its question), and a
 * watch on the profile until the bot reports the link.
 *
 * The watch reads the profile directly and ignores failures: `refreshAuth`
 * signs the user out when a request fails, which a poll must never do.
 */
export function useTelegramLink({ auto = false }: { auto?: boolean } = {}) {
  const { state, applyUser } = useAuth();
  const isLinked = Boolean(state.user?.isTelegramLinked);
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);
  const [failed, setFailed] = useState(false);
  const requested = useRef(false);
  // the context's function may change identity on every render; the watch shouldn't restart for it
  const applyUserRef = useRef(applyUser);
  applyUserRef.current = applyUser;

  const generate = useCallback(async () => {
    setGenerating(true);
    setFailed(false);
    try {
      const result = await authApi.generateTelegramLinkToken();
      setToken(result.token);
      setExpiresAt(new Date(result.expiresAt).getTime());
    } catch {
      setFailed(true);
    } finally {
      setGenerating(false);
    }
  }, []);

  // A code ready as soon as the step shows, once.
  useEffect(() => {
    if (auto && !isLinked && !requested.current) {
      requested.current = true;
      generate();
    }
  }, [auto, isLinked, generate]);

  // An expired code is dropped, and replaced when the code is wanted on its own.
  useEffect(() => {
    if (!expiresAt) return undefined;
    const expire = () => {
      setToken(null);
      setExpiresAt(null);
      if (auto) generate();
    };
    const left = expiresAt - Date.now();
    if (left <= 0) {
      expire();
      return undefined;
    }
    const timer = setTimeout(expire, left);
    return () => clearTimeout(timer);
  }, [expiresAt, auto, generate]);

  // While a code is out, check whether the bot linked it: on a timer, and
  // right away when the person comes back from Telegram.
  useEffect(() => {
    if (!token || isLinked) return undefined;
    let cancelled = false;
    const check = async () => {
      try {
        const { user } = await authApi.getProfile();
        if (!cancelled && user.isTelegramLinked) applyUserRef.current(user);
      } catch {
        // a failed check is just a check that didn't happen
      }
    };
    const interval = setInterval(check, POLL_MS);
    const onReturn = () => {
      if (!document.hidden) check();
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [token, isLinked]);

  return {
    isLinked,
    telegramUsername: state.user?.telegramUsername,
    token,
    deepLink: token ? `${BOT_URL}?start=${token}` : null,
    generating,
    failed,
    generate,
  };
}
